import { View } from "@novorender/api";
import { vec3 } from "gl-matrix";
import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";

import { clippingOutlineLaserActions, LaserDirection, OutlineLaser, selectOutlineLasers } from "../outlineLaserSlice";
import { intersectionToLaserPoint, LASER_POS_Z_OFFSET } from "../utils";

export function useMoveLaserAxis() {
    const {
        state: { view },
    } = useExplorerGlobals(true);
    const outlineLasers = useAppSelector(selectOutlineLasers);
    const dispatch = useAppDispatch();

    return useCallback(
        async ({ laserId, direction, step }: { laserId: string; direction: LaserDirection; step: -1 | 1 }) => {
            const laserIndex = outlineLasers.findIndex((l) => l.id === laserId);
            if (laserIndex === -1) {
                return;
            }
            const laser = outlineLasers[laserIndex];

            let newLaser: OutlineLaser = {
                ...laser,
                measurementX: laser.measurementX && { ...laser.measurementX },
                measurementY: laser.measurementY && { ...laser.measurementY },
                measurementZ: laser.measurementZ && { ...laser.measurementZ },
            };

            const measurement =
                direction === "left" || direction === "right"
                    ? newLaser.measurementX
                    : direction === "up" || direction === "down"
                      ? newLaser.measurementY
                      : newLaser.measurementZ;
            if (!measurement) {
                return;
            }

            const isEnd = direction === "right" || direction === "up" || direction === "zUp";
            const idx = isEnd ? measurement.endIdx : measurement.startIdx;
            if (idx === undefined) {
                return;
            }

            if (step === 1) {
                newLaser = findMoreIntersectionsIfNeeded(view, direction, newLaser, idx);
            }

            const newIdx = Math.max(0, Math.min(newLaser[direction].length - 1, idx + step));
            if (isEnd) {
                measurement.endIdx = newIdx;
            } else {
                measurement.startIdx = newIdx;
            }

            const newOutlineLasers = outlineLasers.with(laserIndex, newLaser);

            dispatch(clippingOutlineLaserActions.setLasers(newOutlineLasers));
        },
        [view, dispatch, outlineLasers],
    );
}

function findMoreIntersectionsIfNeeded(view: View, direction: LaserDirection, laser: OutlineLaser, idx: number) {
    const existingIntersections = laser[direction];
    if (!laser.hasMoreIntersections[direction] || existingIntersections.length === 0) {
        return laser;
    }

    // Only ask for more if we're at the one before the last intersection.
    // If we're before - too early.
    // If at the last one - there shouldn't be more intersections and something is wrong.
    if (idx !== existingIntersections.length - 2) {
        return laser;
    }

    const plane = laser.laserPlanes?.[0];
    const normal = laser.zDir || (plane && vec3.fromValues(plane[0], plane[1], plane[2]));

    if (!normal) {
        return laser;
    }

    const origin = existingIntersections.at(-1)!.position;
    const resultLimit = 1;
    const result = view.threePointLaser({
        origin,
        zDir: normal,
        xDir: laser.xDir,
        [direction]: true,
        resultLimit,
        minDistFromOrigin: 0.001,
        xyOffsets: [-LASER_POS_Z_OFFSET * 2],
    });

    if (!result) {
        return laser;
    }

    const axisResult = result[direction];

    laser = {
        ...laser,
        hasMoreIntersections: {
            ...laser.hasMoreIntersections,
            [direction]: axisResult.length >= resultLimit,
        },
        [direction]: existingIntersections.concat(axisResult.map(intersectionToLaserPoint)),
    };

    return laser;
}
